// src/pages/Screener.jsx
import React, { useState } from 'react';
import AdvancedChart from '../components/AdvancedChart';
import ErrorBoundary from '../components/ErrorBoundary';
import { authFetch } from '../utils/api';

// ── Strategy definitions ──────────────────────────────────────────────────────

const STRATEGIES = [
  {
    key: 'uptrend',
    label: 'Uptrend',
    endpoint: '/api/screener/uptrend',
    desc: 'Stocks in strong uptrends — momentum / trend-following entries',
    icon: '\u2197',
    color: '#22c55e',
    params: [
      { name: 'min_uptrend_days', label: 'Min Uptrend Days', type: 'number', min: 5, max: 60, default: 10 },
      { name: 'rsi_low', label: 'RSI Low', type: 'number', min: 30, max: 70, default: 50 },
      { name: 'rsi_high', label: 'RSI High', type: 'number', min: 60, max: 90, default: 75 },
      { name: 'adx_threshold', label: 'ADX Threshold', type: 'number', min: 15, max: 50, default: 25 },
    ],
  },
  {
    key: 'rsi',
    label: 'RSI < 30',
    endpoint: '/api/screener/rsi',
    desc: 'Oversold stocks — potential bounce candidates',
    icon: '\u2199',
    color: '#f59e0b',
    params: [
      { name: 'threshold', label: 'RSI Threshold', type: 'number', min: 10, max: 50, default: 30 },
      { name: 'window', label: 'RSI Window', type: 'number', min: 5, max: 30, default: 14 },
    ],
  },
  {
    key: 'ha',
    label: 'HA 2-Candle',
    endpoint: '/api/screener/ha',
    desc: 'Heikin-Ashi two-candle reversal pattern',
    icon: '\u26A1',
    color: '#8b5cf6',
    params: [],
  },
  {
    key: 'ha_macd',
    label: 'HA + MACD',
    endpoint: '/api/screener/ha_macd',
    desc: 'Heikin-Ashi reversal confirmed by MACD crossover',
    icon: '\u2728',
    color: '#06b6d4',
    params: [],
  },
  {
    key: 'combined',
    label: 'Combined',
    endpoint: '/api/screener/combined',
    desc: 'Multi-indicator filter: RSI + Volume + EMA',
    icon: '\u2699',
    color: '#3b82f6',
    params: [
      { name: 'rsi_threshold', label: 'RSI Threshold', type: 'number', min: 10, max: 50, default: 30 },
      { name: 'volume_multiplier', label: 'Volume Mult', type: 'number', min: 0.5, max: 3, default: 1.2, step: 0.1 },
    ],
  },
  {
    key: 'uptrend_stop',
    label: 'Uptrend Reversal',
    endpoint: '/api/screener/uptrend_stop',
    desc: 'Uptrending stocks showing reversal signals (swing short)',
    icon: '\u26A0',
    color: '#ef4444',
    params: [
      { name: 'min_uptrend_days', label: 'Min Uptrend Days', type: 'number', min: 5, max: 30, default: 10 },
      { name: 'rsi_overbought', label: 'RSI Overbought', type: 'number', min: 60, max: 80, default: 70 },
    ],
  },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function scoreColor(score, strategy) {
  if (strategy === 'uptrend') {
    if (score >= 9) return '#22c55e';
    if (score >= 7) return '#4ade80';
    return '#86efac';
  }
  if (strategy === 'uptrend_stop') {
    if (score >= 6) return '#ef4444';
    if (score >= 4) return '#f59e0b';
    return '#64748b';
  }
  return '#38bdf8';
}

function scoreRowBg(row, strategy) {
  if (strategy === 'uptrend' && row.score) {
    if (row.score >= 9) return 'rgba(34,197,94,0.08)';
    if (row.score >= 7) return 'rgba(34,197,94,0.04)';
  }
  if (strategy === 'uptrend_stop' && row.signal_score) {
    if (row.signal_score >= 6) return 'rgba(239,68,68,0.08)';
    if (row.signal_score >= 4) return 'rgba(245,158,11,0.06)';
  }
  return '';
}

function formatCell(col, value) {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    if (col.includes('volume') && !col.includes('ratio')) return value.toLocaleString();
    return value.toFixed(2);
  }
  return String(value);
}

// ── Sort Icon ───────────────────────────────────────────────────────────────

function SortIcon({ col, sortCol, sortDir }) {
  if (col !== sortCol)
    return <span style={{ color: '#475569', marginLeft: 4, fontSize: '0.65rem' }}>{'\u21C5'}</span>;
  return (
    <span style={{ marginLeft: 4, color: '#38bdf8', fontSize: '0.7rem' }}>
      {sortDir === 'asc' ? '\u2191' : '\u2193'}
    </span>
  );
}

// ── Results Table ───────────────────────────────────────────────────────────

function ResultsTable({ results, strategy, onChartClick }) {
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('desc');

  if (!results.length) return null;

  const hideCols = ['signal_reasons', 'reasons', 'entry_type', 'strategy_type'];
  const cols = Object.keys(results[0]).filter(k => !hideCols.includes(k));
  const orderedCols = ['symbol', ...cols.filter(c => c !== 'symbol')];

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir('desc'); }
  };

  const sorted = [...results].sort((a, b) => {
    if (!sortCol) return 0;
    const av = a[sortCol], bv = b[sortCol];
    const n = (v) => (typeof v === 'number' ? v : parseFloat(v) || 0);
    const dir = sortDir === 'asc' ? 1 : -1;
    if (typeof av === 'number' || !isNaN(parseFloat(av))) return (n(av) - n(bv)) * dir;
    return String(av || '').localeCompare(String(bv || '')) * dir;
  });

  const reasonsField = strategy === 'uptrend' ? 'reasons' : strategy === 'uptrend_stop' ? 'signal_reasons' : null;

  return (
    <div style={{ borderRadius: 10, border: '1px solid #1e293b', overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
          <thead>
            <tr style={{ background: '#0f172a', borderBottom: '1px solid #334155' }}>
              {orderedCols.map(col => (
                <th
                  key={col}
                  onClick={() => handleSort(col)}
                  style={{
                    padding: '0.6rem 0.7rem',
                    textAlign: col === 'symbol' ? 'left' : 'right',
                    fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8',
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                    cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
                    background: sortCol === col ? 'rgba(56,189,248,0.06)' : undefined,
                    borderBottom: sortCol === col ? '2px solid #38bdf8' : '2px solid transparent',
                  }}
                >
                  {col.replace(/_/g, ' ')}
                  <SortIcon col={col} sortCol={sortCol} sortDir={sortDir} />
                </th>
              ))}
              <th style={{ padding: '0.6rem 0.5rem', fontSize: '0.68rem', color: '#94a3b8', fontWeight: 700 }}>
                CHART
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, i) => {
              const rowBg = scoreRowBg(row, strategy);
              return (
                <tr
                  key={i}
                  style={{
                    borderBottom: '1px solid #1e293b',
                    background: rowBg,
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(56,189,248,0.04)')}
                  onMouseLeave={e => (e.currentTarget.style.background = rowBg)}
                >
                  {orderedCols.map((col, j) => {
                    const val = row[col];
                    const isScore = col === 'signal_score' || col === 'score';
                    const isSymbol = col === 'symbol';
                    const isPass = typeof val === 'boolean' || (col.startsWith('pass_'));
                    return (
                      <td
                        key={j}
                        style={{
                          padding: '0.5rem 0.7rem',
                          textAlign: isSymbol ? 'left' : 'right',
                          fontFamily: isSymbol ? 'inherit' : 'monospace',
                          color: isScore
                            ? scoreColor(val, strategy)
                            : isSymbol
                              ? '#e2e8f0'
                              : isPass
                                ? (val === true || val === 'True' ? '#22c55e' : '#ef4444')
                                : '#cbd5e1',
                          fontWeight: isScore || isSymbol ? 700 : 400,
                        }}
                      >
                        {isScore ? (
                          <span style={{
                            display: 'inline-block',
                            background: `${scoreColor(val, strategy)}18`,
                            padding: '0.15rem 0.5rem',
                            borderRadius: 12,
                            fontSize: '0.78rem',
                          }}>
                            {val}
                          </span>
                        ) : isPass ? (
                          val === true || val === 'True' ? '\u2713' : '\u2717'
                        ) : (
                          formatCell(col, val)
                        )}
                      </td>
                    );
                  })}
                  <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                    <button
                      onClick={() => onChartClick(row.symbol)}
                      title="View chart"
                      style={{
                        padding: '0.2rem 0.55rem', fontSize: '0.72rem',
                        background: 'rgba(56,189,248,0.1)', color: '#38bdf8',
                        border: '1px solid rgba(56,189,248,0.2)', borderRadius: 5,
                        cursor: 'pointer', fontWeight: 600,
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(56,189,248,0.2)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'rgba(56,189,248,0.1)')}
                    >
                      Chart
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Signal / trend reasons expandable section */}
      {reasonsField && results.some(r => r[reasonsField]) && (
        <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid #1e293b', background: '#0c1426' }}>
          <div style={{
            fontSize: '0.68rem', fontWeight: 700, color: '#64748b',
            textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.4rem',
          }}>
            {strategy === 'uptrend' ? 'Trend Reasons' : 'Signal Reasons'}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem 1.25rem' }}>
            {sorted.filter(r => r[reasonsField]).map((r, i) => (
              <div key={i} style={{ fontSize: '0.75rem', lineHeight: 1.6 }}>
                <strong style={{ color: '#e2e8f0' }}>{r.symbol}</strong>
                <span style={{ color: '#64748b', marginLeft: 4 }}>{r[reasonsField]}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Screener() {
  const [strategy, setStrategy] = useState(STRATEGIES[0].key);
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [params, setParams] = useState({});
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);

  const currentStrategy = STRATEGIES.find(s => s.key === strategy);

  const getParamValue = (p) => params[p.name] ?? p.default;

  const setParamValue = (name, value) => {
    setParams(prev => ({ ...prev, [name]: value }));
  };

  const runScreener = async () => {
    setLoading(true); setError(null); setResults([]); setSummary(null);
    setSelectedSymbol(null); setChartData(null);

    let url = currentStrategy.endpoint;
    const qp = new URLSearchParams();
    for (const p of currentStrategy.params) {
      qp.set(p.name, getParamValue(p));
    }
    const qs = qp.toString();
    if (qs) url += '?' + qs;

    try {
      const res = await authFetch(url);
      if (!res.ok) throw new Error(await res.text());
      const body = await res.json();
      setResults(body.results || []);
      if (body.summary) setSummary(body.summary);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadChart = async (symbol) => {
    if (selectedSymbol === symbol) { setSelectedSymbol(null); setChartData(null); return; }
    setChartLoading(true);
    setSelectedSymbol(symbol);
    try {
      const res = await authFetch(`/api/history?${new URLSearchParams({ symbol, resolution: 'D', days: '90' })}`);
      if (!res.ok) throw new Error(await res.text());
      setChartData(await res.json());
    } catch { setChartData(null); }
    finally { setChartLoading(false); }
  };

  const showRSI = strategy === 'uptrend_stop' || strategy === 'uptrend';

  return (
    <div style={{ padding: '1rem', maxWidth: 1200 }}>
      {/* Header */}
      <div style={{ marginBottom: '1.25rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800, color: '#e2e8f0' }}>
          Screener
        </h1>
        <p style={{ margin: '0.25rem 0 0', fontSize: '0.78rem', color: '#64748b' }}>
          {currentStrategy.desc}
        </p>
      </div>

      {/* Strategy cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
        gap: '0.5rem',
        marginBottom: '1rem',
      }}>
        {STRATEGIES.map(s => {
          const active = strategy === s.key;
          return (
            <button
              key={s.key}
              onClick={() => { setStrategy(s.key); setResults([]); setSummary(null); setError(null); setParams({}); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.6rem 0.85rem',
                borderRadius: 10,
                border: active ? `1.5px solid ${s.color}` : '1px solid #334155',
                background: active ? `${s.color}12` : '#1e293b',
                cursor: 'pointer',
                transition: 'all 0.15s',
                textAlign: 'left',
              }}
            >
              <span style={{
                fontSize: '1.1rem',
                width: 28, height: 28,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: 8,
                background: active ? `${s.color}20` : '#0f172a',
              }}>
                {s.icon}
              </span>
              <span style={{
                fontSize: '0.8rem', fontWeight: 700,
                color: active ? s.color : '#94a3b8',
              }}>
                {s.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Parameters + Run */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end',
        gap: '0.75rem',
        padding: '0.75rem 1rem',
        background: '#1e293b',
        borderRadius: 10,
        border: '1px solid #334155',
        marginBottom: '1rem',
      }}>
        {currentStrategy.params.length > 0 ? (
          currentStrategy.params.map(p => (
            <label key={p.name} style={{
              display: 'flex', flexDirection: 'column', gap: 3,
              fontSize: '0.7rem', fontWeight: 600, color: '#64748b',
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}>
              {p.label}
              <input
                type="number"
                min={p.min} max={p.max} step={p.step || 1}
                value={getParamValue(p)}
                onChange={e => setParamValue(p.name, +e.target.value)}
                style={{
                  padding: '0.35rem 0.5rem',
                  fontSize: '0.85rem',
                  border: '1px solid #334155',
                  borderRadius: 6,
                  width: 80,
                  background: '#0f172a',
                  color: '#e2e8f0',
                  outline: 'none',
                }}
              />
            </label>
          ))
        ) : (
          <span style={{ fontSize: '0.78rem', color: '#64748b' }}>No configurable parameters</span>
        )}

        <button
          onClick={runScreener}
          disabled={loading}
          style={{
            padding: '0.5rem 1.5rem',
            background: loading ? '#334155' : currentStrategy.color,
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            cursor: loading ? 'default' : 'pointer',
            fontWeight: 700,
            fontSize: '0.85rem',
            marginLeft: 'auto',
            opacity: loading ? 0.7 : 1,
            transition: 'all 0.15s',
            minWidth: 130,
          }}
        >
          {loading ? 'Running...' : 'Run Screener'}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          background: 'rgba(239,68,68,0.1)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 8,
          padding: '0.65rem 1rem',
          color: '#fca5a5',
          fontSize: '0.82rem',
          marginBottom: '0.85rem',
        }}>
          {error}
        </div>
      )}

      {/* Summary bar */}
      {summary && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '1rem',
          padding: '0.7rem 1rem',
          background: '#1e293b',
          border: '1px solid #334155',
          borderRadius: 10,
          marginBottom: '0.85rem',
        }}>
          {Object.entries(summary).map(([k, v]) => (
            <div key={k} style={{ minWidth: 80 }}>
              <div style={{
                fontSize: '0.65rem', color: '#64748b', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.04em',
              }}>
                {k.replace(/_/g, ' ')}
              </div>
              <div style={{ fontWeight: 700, color: '#e2e8f0', fontSize: '0.95rem' }}>
                {String(v)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Results count */}
      {results.length > 0 && (
        <div style={{
          fontSize: '0.78rem', color: '#64748b', marginBottom: '0.5rem',
          fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem',
        }}>
          <span style={{
            display: 'inline-block',
            background: `${currentStrategy.color}20`,
            color: currentStrategy.color,
            padding: '0.15rem 0.5rem',
            borderRadius: 10,
            fontWeight: 700,
            fontSize: '0.75rem',
          }}>
            {results.length}
          </span>
          stock{results.length !== 1 ? 's' : ''} matched
          <span style={{ color: '#475569' }}>|</span>
          Click headers to sort
          <span style={{ color: '#475569' }}>|</span>
          Click Chart to view
        </div>
      )}

      {/* Results table */}
      {results.length > 0 && (
        <ResultsTable results={results} strategy={strategy} onChartClick={loadChart} />
      )}

      {/* Empty state */}
      {!loading && results.length === 0 && !error && (
        <div style={{
          textAlign: 'center',
          padding: '3rem 1rem',
          color: '#475569',
          background: '#1e293b',
          borderRadius: 10,
          border: '1px solid #334155',
        }}>
          <div style={{ fontSize: '1.8rem', marginBottom: '0.5rem', opacity: 0.5 }}>
            {currentStrategy.icon}
          </div>
          <p style={{ fontSize: '0.88rem', color: '#64748b', margin: 0 }}>
            Select a strategy and click <strong style={{ color: '#94a3b8' }}>Run Screener</strong>
          </p>
        </div>
      )}

      {/* Inline chart */}
      {selectedSymbol && (
        <div style={{
          marginTop: '1.25rem',
          background: '#1e293b',
          borderRadius: 10,
          border: '1px solid #334155',
          padding: '1rem',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#e2e8f0' }}>
              {selectedSymbol} — Daily Chart (90 days)
            </span>
            <button
              onClick={() => { setSelectedSymbol(null); setChartData(null); }}
              style={{
                padding: '0.25rem 0.65rem',
                fontSize: '0.78rem',
                background: '#0f172a',
                border: '1px solid #334155',
                borderRadius: 5,
                cursor: 'pointer',
                color: '#94a3b8',
              }}
            >
              Close
            </button>
          </div>
          {chartLoading && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>Loading chart...</div>
          )}
          {chartData && !chartLoading && (
            <ErrorBoundary>
              <AdvancedChart data={chartData} height={380} showVolume showEMA showRSI={showRSI} />
            </ErrorBoundary>
          )}
          {!chartData && !chartLoading && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
              No chart data for {selectedSymbol}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
